/**
 * Mock market prices used by the lobby to render USD equivalents
 * (`~$12.34`) beneath any non-USDC value.
 *
 * Production wiring: every consumer that has access to the live React
 * tree should pull TAKEOVER and ETH prices from `useTakeoverPrice()` /
 * `useEthPrice()` and pass them explicitly into the `*WithPrice`
 * helpers below. The bare constants (`MOCK_TAKEOVER_USD`,
 * `MOCK_ETH_USD`) only remain for static-context callers (module-load
 * mocks, SSR fallbacks, unit tests) and the `takeoverToUsd` /
 * `takeoverToCompactUsd` shims that wrap them — those shims emit a
 * one-shot dev warning the first time they're called so it's obvious
 * when a renderer is leaking stale prices into the UI.
 */

/** Stale fallback TAKEOVER → USD rate. Live consumers must use `useTakeoverPrice()`. */
export const MOCK_TAKEOVER_USD = 0.11;
/** Stale fallback ETH → USD rate. Live consumers must use `useEthPrice()`. */
export const MOCK_ETH_USD = 3_250;

let warnedTakeoverFallback = false;

function warnFallback(label: string): void {
  if (warnedTakeoverFallback) return;
  warnedTakeoverFallback = true;
  // eslint-disable-next-line no-console -- one-shot dev warning.
  if (typeof console !== 'undefined' && console.warn) {
    console.warn(
      `[mock-prices] ${label} called without an explicit priceUsd — falling back to MOCK_TAKEOVER_USD. Thread useTakeoverPrice() through this code path before production.`,
    );
  }
}

/**
 * Convert a TAKEOVER amount to a `~$X.XX` hint using an explicit live
 * price. Use this from any code path that has access to React hooks.
 */
export function formatTakeoverUsd(amount: number, priceUsd: number, decimals = 2): string {
  const n = (amount || 0) * priceUsd;
  return `~${formatCompactUsd(n, decimals)}`;
}

/**
 * Same shape as `formatTakeoverUsd` but returns just the compact USD
 * string with no `~` prefix.
 */
export function formatTakeoverCompactUsd(amount: number, priceUsd: number): string {
  return formatCompactUsd((amount || 0) * priceUsd);
}

/**
 * @deprecated Prefer `formatTakeoverUsd(amount, priceUsd)`. Retained
 * only for static-context callers (e.g. `mock-adapters.ts`'s Boardroom
 * fallback) that don't have a hook-fed price on hand. Logs a one-shot
 * warning the first time it's called.
 */
export function takeoverToUsd(amount: number, decimals = 2): string {
  warnFallback('takeoverToUsd');
  return formatTakeoverUsd(amount, MOCK_TAKEOVER_USD, decimals);
}

/** Convert arbitrary number to a `~$X.XX` hint. */
export function toUsdHint(usd: number, decimals = 2): string {
  if (!Number.isFinite(usd)) return '';
  return `~${formatCompactUsd(usd, decimals)}`;
}

/**
 * Compact USD formatter used for tile price chips and header badges. The
 * output is always short (≤6 chars incl. `$`) so labels don't blow out tiny
 * UI elements. Ranges:
 *
 *   < $0.01   → `<$0.01`
 *   < $10     → `$3.45`     (2 decimals)
 *   < $100    → `$12.30`
 *   < $1K     → `$123`      (rounded)
 *   < $1M     → `$1.23K`    (2 decimals, uppercase K)
 *   ≥ $1M     → `$1.23M`
 */
export function formatCompactUsd(usd: number, decimals = 2): string {
  if (!Number.isFinite(usd)) return '';
  if (usd <= 0) return `$0.00`;
  if (usd < 0.01) return `<$0.01`;
  if (usd >= 1_000_000) return `$${(usd / 1_000_000).toFixed(2)}M`;
  if (usd >= 1_000) return `$${(usd / 1_000).toFixed(2)}K`;
  if (usd >= 100) return `$${Math.round(usd)}`;
  if (usd >= 10) return `$${usd.toFixed(2)}`;
  return `$${usd.toFixed(decimals)}`;
}

/**
 * Board price-pill / tile-strip USD format. Kept output-identical to
 * `formatCompactUsd` (the 2D board formatter) so the 3D pills and the 2D chips
 * always read the same number. Cents are shown below $100 (NOT rounded at
 * $10–$99.99 — that rounding was removed to match the 2D board), whole dollars
 * from $100–$999, then compact K/M:
 *   ≤ $0      → `$0`
 *   < $0.01   → `<$0.01`
 *   < $100    → `$3.45` / `$12.30`   (2 decimals)
 *   < $1K     → `$640`               (integer)
 *   < $1M     → `$1.23K`
 *   ≥ $1M     → `$1.23M`
 */
export function formatBoardUsd(usd: number): string {
  if (!Number.isFinite(usd)) return '';
  if (usd <= 0) return '$0';
  if (usd < 0.01) return '<$0.01';
  if (usd < 100) return `$${usd.toFixed(2)}`;
  if (usd < 1_000) return `$${Math.round(usd)}`;
  if (usd < 1_000_000) return `$${(usd / 1_000).toFixed(2)}K`;
  return `$${(usd / 1_000_000).toFixed(2)}M`;
}

/**
 * @deprecated Prefer `formatTakeoverCompactUsd(amount, priceUsd)`.
 */
export function takeoverToCompactUsd(amount: number): string {
  warnFallback('takeoverToCompactUsd');
  return formatTakeoverCompactUsd(amount, MOCK_TAKEOVER_USD);
}

/**
 * Format a seat index as a zero-padded 3-digit string (e.g. 0 → "000", 12 → "012").
 * The lobby replaces the old letter-coordinate style ("C7") with pure numerics
 * so indexing stays consistent across grids of any size.
 */
export function formatSeatId(index: number): string {
  return index.toString().padStart(3, '0');
}
